import { fetcher } from '@commenda/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Shapes. These mirror the backend contract exactly; display labels are the
// component's business, not the API's.
// ---------------------------------------------------------------------------

export type UpsellTriggerType =
  | 'RENEWAL'
  | 'PRODUCT_SUITE'
  | 'ECONOMIC_EXPOSURE'
  | 'SALES_TAX_VOLUME'
  | 'BOOKKEEPING_VOLUME'
  | 'GENERAL_INFORMATION';

export type UpsellSuite =
  | 'DIRECT_TAX'
  | 'INDIRECT_TAX'
  | 'TRANSFER_PRICING'
  | 'ENTITY_MANAGEMENT'
  | 'BOOKKEEPING';

export type UpsellOpportunityStatus =
  | 'OPEN'
  | 'EMAILED'
  | 'SNOOZED'
  | 'WON'
  | 'LOST'
  | 'LATER'
  | 'IRRELEVANT';

export type UpsellOutcomeReason =
  | 'TOO_EXPENSIVE'
  | 'OTHER_PROVIDER'
  | 'NO_BUDGET'
  | 'WRONG_TIME'
  | 'NOT_A_FIT'
  | 'NO_REPLY'
  | 'IRRELEVANT_SIGNAL'
  | 'SOMETHING_ELSE';

export type UpsellEventType =
  | 'CREATED'
  | 'SURFACED'
  | 'EMAIL_SENT'
  | 'SNOOZED'
  | 'STATUS_CHANGED'
  | 'DEAL_LINKED'
  | 'DEAL_REJECTED'
  | 'MARKED_IRRELEVANT'
  | 'UNDONE';

export type UpsellDealCheckResult =
  | 'OK'
  | 'DEAL_NOT_FOUND'
  | 'DEAL_HAS_NO_COMPANY'
  | 'COMPANY_MISMATCH'
  | 'COMMENDA_COMPANY_NOT_LINKED';

export interface IUpsellEvidence {
  services?: string;
  renewsOn?: string;
  subscriptionCount?: number;
  worksWithUsOn?: UpsellSuite[];
  missing?: UpsellSuite[];
  entityLegalName?: string | null;
  entityCountry?: string;
  requestCount?: number;
  siblingEntityHasSuite?: boolean;
  registeredJurisdictionCount?: number;
  breachedJurisdictions?: string[];
  approachingJurisdictions?: string[];
  annualizedTransactions?: number;
  rawTransactions?: number;
  daysMeasured?: number;
  tooNewToAnnualize?: boolean;
  currentTier?: string;
  nextTier?: string;
  percentOfTier?: number;
  transactionsPerMonth?: number;
  signalKind?: string;
  finding?: string;
  sourceLabel?: string;
  observedAt?: string | null;
}

export interface IUpsellEntity {
  corporationId: number;
  legalName: string;
  country: string;
}

export interface IUpsellHubSpotLink {
  connected: boolean;
  hubspotCompanyId: string | null;
  hubspotUrl: string | null;
}

export interface IUpsellRow {
  opportunityId: string;
  signalKey: string;
  companyId: number;
  companyName: string;
  adminUrl: string;
  entity: IUpsellEntity | null;
  trigger: UpsellTriggerType;
  status: UpsellOpportunityStatus;
  dueInDays: number | null;
  /** When a snoozed, emailed or LATER row comes back. Past means it is due now. */
  surfaceAt: string;
  suites: UpsellSuite[];
  evidence: IUpsellEvidence;
  hubspot: IUpsellHubSpotLink;
  hubspotDealId: string | null;
  snoozeCount: number;
  emailCount: number;
  lastEventId: string | null;
  lastEventType: UpsellEventType | null;
  lastEventAt: string | null;
}

export interface IUpsellSection {
  trigger: UpsellTriggerType;
  rows: IUpsellRow[];
  openCount: number;
  totalCount: number;
  unavailableReason?: string;
}

export interface IUpsellAccountManager {
  userId: string;
  name: string;
  email: string;
  accountCount: number;
}

export interface IUpsellQueueResponse {
  accountManager: IUpsellAccountManager;
  sections: IUpsellSection[];
  unlinkedCompanyCount: number;
}

export interface IUpsellAccountManagerListResponse {
  accountManagers: IUpsellAccountManager[];
  canSwitchBooks: boolean;
}

export interface IUpsellActivityEntry {
  eventId: string;
  opportunityId: string;
  type: UpsellEventType;
  at: string;
  companyId: number;
  companyName: string;
  adminUrl: string;
  trigger: UpsellTriggerType;
  summary: string;
  undoable: boolean;
}

export interface IUpsellActivityResponse {
  accountManager: IUpsellAccountManager;
  entries: IUpsellActivityEntry[];
}

export interface IUpsellDealVerification {
  result: UpsellDealCheckResult;
  ok: boolean;
  message: string;
  parsedDealId: string | null;
  dealName: string | null;
  dealCompanyId: string | null;
  dealCompanyName: string | null;
  expectedCompanyId: string | null;
  expectedCompanyName: string | null;
}

export interface IUpsellActionResponse {
  row: IUpsellRow;
  eventId: string;
}

/** What the engine actually runs on. Every value is read, never typed into the client. */
export interface IUpsellTriggerSettings {
  renewalLeadDays: number;
  /** The default, used by any trigger with no row of its own. */
  followUpDays: number;
  /** What each trigger actually waits, which is what the email actions read. */
  followUpDaysByTrigger: Record<UpsellTriggerType, number>;
  undoWindowHours: number;
  sweepSchedule: string;
  editableInApp: boolean;
}

export interface IUpsellHubSpotCandidate {
  hubspotCompanyId: string;
  name: string | null;
  domain: string | null;
  reason: string;
}

export const upsellKeys = {
  ACCOUNT_MANAGERS: 'UPSELL_ACCOUNT_MANAGERS',
  QUEUE: 'UPSELL_QUEUE',
  ACTIVITY: 'UPSELL_ACTIVITY',
  HUBSPOT_CANDIDATES: 'UPSELL_HUBSPOT_CANDIDATES',
  SETTINGS: 'UPSELL_SETTINGS',
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

async function get<T>(path: string, failure: string): Promise<T> {
  const response = await fetcher(path, { method: 'GET' });
  if (!response.ok) throw new Error(failure);
  return response.json();
}

async function post<T>(path: string, body: object): Promise<T> {
  const response = await fetcher(path, { method: 'POST', body });
  if (!response.ok) {
    // The backend puts the reason in `message`; surfacing it verbatim is the
    // whole point of the deal checks.
    const problem = await response.json().catch(() => null);
    throw new Error(problem?.message ?? 'That did not go through.');
  }
  return response.json();
}

export const useUpsellAccountManagers = () =>
  useQuery<IUpsellAccountManagerListResponse>({
    queryKey: [upsellKeys.ACCOUNT_MANAGERS],
    queryFn: () =>
      get('admin/upsells/account-managers', 'Could not load account managers'),
  });

export const useUpsellQueue = (accountManagerUserId?: string) =>
  useQuery<IUpsellQueueResponse>({
    queryKey: [upsellKeys.QUEUE, accountManagerUserId ?? 'me'],
    queryFn: () =>
      get(
        accountManagerUserId
          ? `admin/upsells/queue?accountManagerUserId=${encodeURIComponent(accountManagerUserId)}`
          : 'admin/upsells/queue',
        'Could not load the upsell queue'
      ),
  });

export const useUpsellActivity = (accountManagerUserId?: string) =>
  useQuery<IUpsellActivityResponse>({
    queryKey: [upsellKeys.ACTIVITY, accountManagerUserId ?? 'me'],
    queryFn: () =>
      get(
        accountManagerUserId
          ? `admin/upsells/activity?accountManagerUserId=${encodeURIComponent(accountManagerUserId)}`
          : 'admin/upsells/activity',
        'Could not load activity'
      ),
  });

export const useUpsellSettings = () =>
  useQuery<IUpsellTriggerSettings>({
    queryKey: [upsellKeys.SETTINGS],
    queryFn: () => get('admin/upsells/settings', 'Could not load settings'),
  });

export const useUpsellHubSpotCandidates = (
  companyId: number | null,
  enabled: boolean
) =>
  useQuery<IUpsellHubSpotCandidate[]>({
    queryKey: [upsellKeys.HUBSPOT_CANDIDATES, companyId],
    enabled: enabled && Boolean(companyId),
    queryFn: () =>
      get(
        `admin/upsells/companies/${companyId}/hubspot-candidates`,
        'Could not search HubSpot'
      ),
  });

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Read-only preview of a pasted deal. Safe to call as the user types, because
 * it writes nothing on either side.
 */
export const verifyUpsellDeal = (opportunityId: string, dealRef: string) =>
  post<IUpsellDealVerification>(
    `admin/upsells/${opportunityId}/verify-deal`,
    { dealRef }
  );

function useUpsellAction<TBody>(path: (id: string) => string) {
  const queryClient = useQueryClient();

  return useMutation<
    IUpsellActionResponse,
    Error,
    { opportunityId: string } & TBody
  >({
    mutationFn: ({ opportunityId, ...body }) =>
      post(path(opportunityId), body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [upsellKeys.QUEUE] });
      void queryClient.invalidateQueries({ queryKey: [upsellKeys.ACTIVITY] });
    },
  });
}

export const useUpsellEmailSent = () =>
  useUpsellAction<{
    suites: UpsellSuite[];
    dealRef: string;
    note?: string;
    actionKey: string;
  }>((id) => `admin/upsells/${id}/email-sent`);

export const useUpsellSnooze = () =>
  useUpsellAction<{ until: string; actionKey: string }>(
    (id) => `admin/upsells/${id}/snooze`
  );

export const useUpsellStatus = () =>
  useUpsellAction<{
    status: UpsellOpportunityStatus;
    reason?: UpsellOutcomeReason;
    reasonNote?: string;
    dealRef: string;
    actionKey: string;
  }>((id) => `admin/upsells/${id}/status`);

export const useUpsellMarkIrrelevant = () =>
  useUpsellAction<{ reason: string; actionKey: string }>(
    (id) => `admin/upsells/${id}/irrelevant`
  );

export const useUpsellUndo = () =>
  useUpsellAction<{ eventId: string; actionKey: string }>(
    (id) => `admin/upsells/${id}/undo`
  );

export const useLinkUpsellHubSpotCompany = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean },
    Error,
    { companyId: number; hubspotCompanyId: string }
  >({
    // The admin route, not an upsell-only one: it verifies the HubSpot company exists
    // and rejects an id already linked elsewhere, which the upsell path never did.
    mutationFn: ({ companyId, hubspotCompanyId }) =>
      post<{ success: boolean }>(
        `admin/companies/${companyId}/external-connections/hubspot/link`,
        { hubspotCompanyId }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [upsellKeys.QUEUE] });
    },
  });
};
